import type { DocNode } from '@atlaskit/adf-schema';
import type { JSONDocNode, JSONNode } from '@atlaskit/editor-json-transformer';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { PageContentCommandResult } from '@atlassian/rovo-content-bridge-api-commands/commands/read-content-command';
import type { CommandProperties } from '@atlassian/rovo-content-bridge-api/command';

import { log } from '../utils/aifc-logging';
import { getDocument } from '../utils/bridge-document-utils';

import { CommandHandler, type PassThroughCommandProps } from './command-handler';

/**
 * Extracts the Maui content ID from the selected ADF fragment.
 */
const extractMauiContentId = (
	selectionFragment: JSONNode[] | null | undefined,
): string | undefined => {
	if (!Array.isArray(selectionFragment) || selectionFragment.length === 0) {
		return undefined;
	}

	const mauiApp = selectionFragment.find(
		(node) =>
			node.type === 'extension' &&
			(node.attrs as { extensionKey?: string } | undefined)?.extensionKey === 'native-embed:maui',
	);
	const attrs = mauiApp?.attrs as
		| { extensionKey?: string; parameters?: { macroParams?: { url?: { value?: string } } } }
		| undefined;

	const url: string | undefined = attrs?.parameters?.macroParams?.url?.value;
	if (url) {
		// We only want the ID, which is the part after /remix/ui/
		const match = new RegExp('remix\\/ui\\/([^/?#]+)', 'u').exec(url);
		return match?.[1];
	}
	return undefined;
};

export const buildContentContext = async ({
	props,
	doc,
	selectionFragment,
	selectionLocalIds,
	selectionMarkdown,
	contentMauiId,
	dynamicUiSource,
}: {
	contentMauiId?: string;
	doc: DocNode | JSONDocNode | undefined;
	dynamicUiSource?: PageContentCommandResult['dynamicUiSource'];
	props: PassThroughCommandProps;
	selectionFragment: string;
	selectionLocalIds: string;
	selectionMarkdown: string;
}): Promise<PageContentCommandResult> => {
	let additionalContextFromProps = props.additionalContext || { isViewMode: false };
	if (typeof additionalContextFromProps === 'function') {
		if (fg('platform_editor_ai_aifc_patch_ga_blockers')) {
			try {
				additionalContextFromProps = await additionalContextFromProps();
				// eslint-disable-next-line no-unused-vars
			} catch (err) {
				additionalContextFromProps = {};
			}
		} else {
			additionalContextFromProps = await additionalContextFromProps();
		}
	}
	const { isViewMode = false, ...additionalContext } = additionalContextFromProps || {};

	return {
		contentId: props.contentId,
		contentType: props.contentType,
		contentMauiId,
		dynamicUiSource,
		pageBody: doc,
		selectionFragment,
		selectionLocalIds,
		selectionMarkdown,
		isViewMode,
		useGenericEditorSkill: props.useGenericEditorSkill,
		additionalContext: Object.keys(additionalContext).length > 0 ? additionalContext : undefined,
	};
};

export class ReadContentCommandHandler extends CommandHandler<
	CommandProperties,
	PageContentCommandResult
> {
	async execute(): Promise<PageContentCommandResult> {
		const defaultResult: PageContentCommandResult = await buildContentContext({
			props: this.props,
			doc: undefined,
			selectionFragment: '',
			selectionLocalIds: '',
			selectionMarkdown: '',
		});

		try {
			const doc = fg('platform_editor_aifc_rovo_bridge_partial_doc')
				? this.props.editorAPI?.aiExperience?.actions.getPartialDocumentFromSelection()
				: fg('platform_editor_ai_aifc_patch_ga_blockers')
					? await this.props.editorAPI.core.actions.requestDocumentAsync()
					: await getDocument(this.props.editorAPI);
			if (!doc) {
				// TODO: EDITOR-123 - report this error
				return defaultResult;
			}

			const selection = this.props.editorAPI.aiExperience?.actions.getAISelectionContext();
			const selectionFragment = selection?.selectionFragment
				? JSON.stringify(selection.selectionFragment)
				: '';

			const content: PageContentCommandResult = await buildContentContext({
				props: this.props,
				doc,
				selectionFragment,
				selectionLocalIds: selection?.localIds ? JSON.stringify(selection.localIds) : '',
				selectionMarkdown: selection?.selectionMarkdown ?? '',
				...(expValEquals('cc-maui-experiment', 'isEnabled', true)
					? {
							contentMauiId: extractMauiContentId(selection?.selectionFragment),
							dynamicUiSource: {
								contentType: 'page',
								startLocalId: selection?.localIds?.[0],
								endLocalId: selection?.localIds?.[selection.localIds.length - 1],
								startIndex: selection?.startIndex ?? undefined,
								endIndex: selection?.endIndex ?? undefined,
								fragmentAdf: selectionFragment,
								fullAdf: doc,
							},
						}
					: {}),
			});

			log('handle read-content-command result', {
				additionalContext: this.props.additionalContext,
				doc,
				selection,
				content,
			});

			return content ?? defaultResult;
		} catch {
			// TODO: EDITOR-123 - report this error
			return defaultResult;
		}
	}
}
